import React, { useContext } from 'react';
import Quotebar from './Quotebar';
import { HelpIconInterface, MicrocopyResource } from '../../../../utilities/contentful';
import { QuoteRequest } from '../../../../components/quote/QuoteRequestData';
import { LocalizationProvider } from '@mui/x-date-pickers';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { Formik, FormikProps } from 'formik';
import { QuoteRequestValidation } from '../../../../components/quote/QuoteRequestFormValidation';
import { submitQuoteForm, updateCoverageAreaText, updateDateText, updatePeopleTab, updateTripProtectionText } from '../../../../components/quote/Form/QuoteHelpers';
import AppContext from '../../../../AppContext';
import FormikPersist from '../../../../components/common/FormikPersist';
import { FullQuoteResult } from '../../../../components/quote/QuoteData.interface';
import { initialDetailsValues, PurchaseDetails } from '../../../../components/purchase/PurchaseData.interface';

interface Props {
  quoteMicrocopy: MicrocopyResource[];
  restoredQuote: QuoteRequest | null;
  setQuoteData: React.Dispatch<React.SetStateAction<Partial<FullQuoteResult>| null>>;
  helpIcons: HelpIconInterface[];
  agentNum: string;
  affiliate?: string;
}

const Topbar = ({ quoteMicrocopy, restoredQuote, setQuoteData, helpIcons, agentNum, affiliate}:Props): JSX.Element => {
  const validationSchema = new QuoteRequestValidation(quoteMicrocopy);
  
  const appState: any = useContext(AppContext);
  
  const submitForm = async (values: PurchaseDetails) => {
    appState.setAppState({
      ...appState.state,
      loadingQuoteResult: true,
      quoteModalOpen: false,
    });
    
    const quoteResult = await submitQuoteForm({ ...values, saveQuote: true }, agentNum);
    
    if (quoteResult.errors || quoteResult.error) {
      setQuoteData(null);
      
      setQuoteBoxDetails({
        coverageDuration: `${updateDateText(values.startDate, undefined, 'Start')} ➜ ${updateDateText(values.endDate, undefined, 'End')}`,
        coverageArea: updateCoverageAreaText(values.usaTrip, values.countryObject),
        tripProtection: updateTripProtectionText(values.tripProtection),
        numPeople: updatePeopleTab(values.primaryInsured, undefined)
      })
      
      appState.setAppState({
        ...appState.state,
        quoteRequestData: values,
        quoteModalOpen: false,
        loadingQuoteResult: false,
        quoteRequestError: true,
      });
      
    } else {
      setQuoteData(quoteResult);
    
      setQuoteBoxDetails({
        coverageDuration: `${updateDateText(values.startDate, undefined, 'Start')} ➜ ${updateDateText(values.endDate, undefined, 'End')}`,
        coverageArea: updateCoverageAreaText(values.usaTrip, values.countryObject),
        tripProtection: updateTripProtectionText(values.tripProtection),
        numPeople: updatePeopleTab(values.primaryInsured, undefined)
      })
  
      appState.setAppState({
        ...appState.state,
        quoteRequestData: values,
        quoteModalOpen: false,
        loadingQuoteResult: false,
        quoteRequestError: false,
      });
    }
  };

  const [quoteBoxDetails, setQuoteBoxDetails] = React.useState({
    coverageDuration: `${updateDateText(appState.state.quoteRequestData.startDate, undefined, 'Start')} ➜ ${updateDateText(appState.state.quoteRequestData.endDate, undefined, 'End')}`,
    coverageArea: updateCoverageAreaText(appState.state.quoteRequestData.usaTrip, appState.state.quoteRequestData.countryObject),
    tripProtection: updateTripProtectionText(appState.state.quoteRequestData.tripProtection),
    numPeople: appState.state.quoteRequestData.primaryInsured ? updatePeopleTab(appState.state.quoteRequestData.primaryInsured, undefined) : 'Who needs coverage?'
  })

  React.useEffect(() => {
    setQuoteBoxDetails({
      coverageDuration: `${updateDateText(appState.state.quoteRequestData.startDate, undefined, 'Start')} ➜ ${updateDateText(appState.state.quoteRequestData.endDate, undefined, 'End')}`,
      coverageArea: updateCoverageAreaText(appState.state.quoteRequestData.usaTrip, appState.state.quoteRequestData.countryObject),
      tripProtection: updateTripProtectionText(appState.state.quoteRequestData.tripProtection),
      numPeople: appState.state.quoteRequestData.primaryInsured ? updatePeopleTab(appState.state.quoteRequestData.primaryInsured, undefined) : 'Who needs coverage?'
    })

  }, [
    appState.state.quoteRequestData.countryObject,
    appState.state.quoteRequestData.endDate,
    appState.state.quoteRequestData.primaryInsured,
    appState.state.quoteRequestData.startDate,
    appState.state.quoteRequestData.tripProtection,
    appState.state.quoteRequestData.usaTrip,
  ]);

  return (
   


      <LocalizationProvider dateAdapter={AdapterDateFns} >
        <Formik
          initialValues={initialDetailsValues as PurchaseDetails}
          validationSchema={validationSchema.quoteValidationSchema}
          onSubmit={submitForm}
          validateOnChange={false}
        >

          {(formikProps: FormikProps<PurchaseDetails>) => {
            return (
              <>
                <FormikPersist name="quote-form" />
                <Quotebar 
                  helpIcons={helpIcons} 
                  microcopy={quoteMicrocopy} 
                  formikProps={formikProps} 
                  restoredQuote={restoredQuote} 
                  quoteBoxDetails={quoteBoxDetails} 
                  setQuoteBoxDetails={setQuoteBoxDetails} 
                  setQuoteData={setQuoteData}
                  agentNum={agentNum}
                  affiliate={affiliate}
                />
              </>
            )
          }}
        </Formik>
      </LocalizationProvider>

  );
};

export default Topbar;
